<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Data Subject Rights</h5>
        <p class="page-sub">Search, export or erase visitor data in response to GDPR / NDPA data subject requests</p>
      </div>
    </div>

    <div class="dsr-info-bar">
      <font-awesome-icon icon="info-circle" class="info-icon" />
      <p>Use this tool to handle visitor data requests. Search by email to locate a data subject, then export their data pack or erase all personal information. Erasure is permanent and irreversible.</p>
    </div>

    <!-- Search -->
    <div class="card search-card">
      <div class="card-title">Search by Email</div>
      <div style="display:flex;gap:10px;flex-wrap:wrap;">
        <input v-model="searchEmail" type="email" class="form-input" style="max-width:340px;" placeholder="visitor@example.com" @keyup.enter="doSearch" />
        <button class="btn-primary-action" @click="doSearch" :disabled="searching">
          <font-awesome-icon v-if="searching" icon="spinner" spin />
          {{ searching ? 'Searching…' : 'Search' }}
        </button>
      </div>
    </div>

    <!-- Results -->
    <div v-if="results.length" class="results-list">
      <div v-for="v in results" :key="v.id" class="result-card">
        <div class="result-header">
          <div class="visitor-av">{{ initials(v) }}</div>
          <div class="result-info">
            <div class="result-name">{{ v.first_name }} {{ v.last_name }}</div>
            <div class="result-sub">{{ v.email }} {{ v.phone ? `· ${v.phone}` : '' }} {{ v.company ? `· ${v.company}` : '' }}</div>
            <div class="result-sub">{{ v.visits_count }} visit{{ v.visits_count !== 1 ? 's' : '' }} · First: {{ v.first_visit ?? '—' }} · Last: {{ v.last_visit ?? '—' }}</div>
          </div>
          <div class="result-actions">
            <button class="btn-ghost" @click="doExport(v)" :disabled="exportingId === v.id">
              <font-awesome-icon :icon="exportingId === v.id ? 'spinner' : 'file-export'" :spin="exportingId === v.id" />
              Export Data
            </button>
            <button class="btn-danger-outline" @click="eraseTarget = v">
              <font-awesome-icon icon="trash-alt" /> Erase
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="searched && !results.length" class="empty-state">
      <font-awesome-icon icon="user-slash" class="empty-icon" />
      <p class="empty-title">No visitor found</p>
      <p class="empty-sub">No visitor profile matches <strong>{{ lastSearchEmail }}</strong>. They may have been previously erased or never registered.</p>
    </div>

    <!-- Erase Confirm Modal -->
    <div v-if="eraseTarget" class="modal-backdrop" @mousedown.self="eraseTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header erase-header">
          <h6 class="modal-title erase-title"><font-awesome-icon icon="exclamation-triangle" /> Confirm Erasure</h6>
          <button class="modal-close" @click="eraseTarget = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p style="font-size:13px;color:#4a5e78;margin:0 0 12px;">You are about to permanently erase all personal data for:</p>
          <div class="erase-subject-card">
            <strong>{{ eraseTarget.first_name }} {{ eraseTarget.last_name }}</strong>
            <span>{{ eraseTarget.email }}</span>
          </div>
          <div class="erase-warning">
            <font-awesome-icon icon="exclamation-circle" />
            <p>This will anonymise all visit records and delete signatures, screening results and the visitor profile. <strong>This action cannot be undone.</strong></p>
          </div>
          <div class="form-row" style="margin-top:14px;">
            <label class="form-label">Type ERASE to confirm</label>
            <input v-model="eraseConfirmText" class="form-input" placeholder="ERASE" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="eraseTarget = null; eraseConfirmText = ''">Cancel</button>
          <button class="btn-danger" @click="doErase" :disabled="erasing || eraseConfirmText !== 'ERASE'">
            <font-awesome-icon v-if="erasing" icon="spinner" spin />
            {{ erasing ? 'Erasing…' : 'Permanently Erase' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'

export default {
  name: 'VisitorDSR',
  setup() { return { toast: useToast() } },
  data() {
    return {
      searchEmail: '', searching: false, searched: false, lastSearchEmail: '',
      results: [],
      exportingId: null,
      eraseTarget: null, eraseConfirmText: '', erasing: false,
    }
  },
  methods: {
    initials(v) { return ((v.first_name?.[0] ?? '') + (v.last_name?.[0] ?? '')).toUpperCase() || '?' },
    async doSearch() {
      if (!this.searchEmail.trim()) { this.toast.error('Enter an email address.'); return }
      this.searching = true; this.searched = false; this.results = []
      this.lastSearchEmail = this.searchEmail
      try {
        const r = await axios.get('visitors/dsr/search', { params: { email: this.searchEmail } })
        this.results = r.data.data ?? r.data
        this.searched = true
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Search failed.') }
      finally { this.searching = false }
    },
    async doExport(v) {
      this.exportingId = v.id
      try {
        const r = await axios.get(`visitors/dsr/${v.id}/export`, { responseType: 'blob' })
        const url = URL.createObjectURL(new Blob([r.data], { type: 'application/json' }))
        const a = document.createElement('a'); a.href = url
        a.download = `visitor-data-export-${v.id.slice(0, 8)}.json`
        document.body.appendChild(a); a.click(); document.body.removeChild(a)
        URL.revokeObjectURL(url)
        this.toast.success('Data exported.')
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Export failed.') }
      finally { this.exportingId = null }
    },
    async doErase() {
      if (this.eraseConfirmText !== 'ERASE') return
      this.erasing = true
      try {
        await axios.delete(`visitors/dsr/${this.eraseTarget.id}/erase`)
        this.toast.success('Visitor data erased.')
        this.results = this.results.filter(v => v.id !== this.eraseTarget.id)
        this.eraseTarget = null; this.eraseConfirmText = ''
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Erasure failed.') }
      finally { this.erasing = false }
    },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.dsr-info-bar { display:flex; align-items:flex-start; gap:10px; background:#eff6ff; border:1px solid #bfdbfe; border-radius:10px; padding:14px 16px; margin-bottom:20px; }
.info-icon { color:#1d4ed8; font-size:16px; margin-top:1px; flex-shrink:0; }
.dsr-info-bar p { font-size:13px; color:#1e40af; margin:0; line-height:1.5; }

.search-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:20px; margin-bottom:20px; }
.card-title  { font-size:13px; font-weight:700; color:#1a2b45; margin-bottom:12px; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-ghost   { display:inline-flex; align-items:center; gap:6px; height:38px; padding:0 16px; border:1px solid #dde5f0; border-radius:8px; background:#fff; font-size:13px; color:#3d5068; cursor:pointer; font-family:inherit; }
.btn-ghost:hover { background:#f4f7fb; }
.btn-danger-outline { display:inline-flex; align-items:center; gap:6px; height:38px; padding:0 16px; border:1px solid #fca5a5; border-radius:8px; background:#fff; font-size:13px; color:#dc2626; cursor:pointer; font-family:inherit; }
.btn-danger-outline:hover { background:#fff5f5; }
.btn-danger  { display:inline-flex; align-items:center; gap:6px; background:#dc2626; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-danger:disabled { opacity:.6; cursor:not-allowed; }

.results-list { display:flex; flex-direction:column; gap:12px; }
.result-card  { background:#fff; border:1px solid #e4eaf2; border-radius:14px; overflow:hidden; }
.result-header { display:flex; align-items:flex-start; gap:14px; padding:16px 20px; flex-wrap:wrap; }
.visitor-av { width:44px; height:44px; border-radius:50%; background:#004AAD; color:#fff; display:flex; align-items:center; justify-content:center; font-size:14px; font-weight:700; flex-shrink:0; }
.result-info { flex:1; }
.result-name { font-size:15px; font-weight:700; color:#1a2b45; margin-bottom:3px; }
.result-sub  { font-size:12px; color:#7a90b0; margin-top:2px; }
.result-actions { display:flex; gap:8px; align-items:flex-start; flex-shrink:0; flex-wrap:wrap; }

.empty-state { text-align:center; padding:60px 20px; }
.empty-icon  { font-size:48px; color:#dde5f0; margin-bottom:16px; }
.empty-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
.empty-sub   { font-size:13px; color:#7a90b0; margin:0; }

.modal-backdrop { position:fixed; inset:0; background:rgba(10,20,40,.45); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
.modal-box { background:#fff; border-radius:16px; width:100%; max-width:440px; max-height:90vh; overflow-y:auto; display:flex; flex-direction:column; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--sm { max-width:440px; }
.modal-header { display:flex; align-items:center; justify-content:space-between; padding:18px 22px 14px; border-bottom:1px solid #f0f4f8; }
.erase-header { background:#fff5f5; }
.modal-title  { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
.erase-title  { color:#dc2626; }
.modal-close  { background:none; border:none; font-size:16px; color:#7a90b0; cursor:pointer; padding:4px; }
.modal-body   { padding:22px; }
.modal-footer { padding:14px 22px; border-top:1px solid #f0f4f8; display:flex; justify-content:flex-end; gap:10px; }

.erase-subject-card { background:#f4f7fb; border-radius:8px; padding:12px 14px; display:flex; flex-direction:column; gap:3px; margin-bottom:14px; }
.erase-subject-card strong { font-size:14px; font-weight:700; color:#1a2b45; }
.erase-subject-card span   { font-size:12px; color:#7a90b0; }
.erase-warning { background:#fff5f5; border:1px solid #fca5a5; border-radius:8px; padding:12px 14px; display:flex; align-items:flex-start; gap:8px; font-size:12px; color:#7f1d1d; }
.erase-warning svg { flex-shrink:0; margin-top:2px; }
.erase-warning p { margin:0; line-height:1.5; }

.form-row  { display:flex; flex-direction:column; gap:5px; }
.form-label { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; font-family:inherit; }
.form-input:focus { border-color:#dc2626; }
</style>
